<template>
  <div :id="`${newAddress.type}-form`"
      class="address-form"
      :class="`col-sm-12 ${formIsSelected ? 'selected' : ''}`"
      :ref="`${addressType}AddForm`">
    <div class="alert alert-danger color-error msgblock" v-if="formErrors.length > 0">
      <ul style="padding-left: 20px;">
        <li v-for="(message, index) in formErrors" :key="index">
          {{ message }}
        </li>
      </ul>
    </div>

    <div class="form-group" v-if="addressType === 'shipping'">
      <label>Company / Personal (First Last)*</label>
      <input type="text" name="name" class="form-control" :class="{'has-error' : errors.has('name')}" maxlength="30"
            v-validate="'required'"
            v-model="newAddress.name.given_name"
            autocomplete="name"
            @input="newAddress.name.given_name = formatName($event.target.value)">
      <span v-show="errors.has('name')" class="text-danger">{{ errors.first('name') }}</span>
    </div>

    <div class="row" v-else>
      <div class="form-group col-sm-8">
        <label>Company / Personal (First Last)*</label>
        <input type="text" name="name" class="form-control" :class="{'has-error' : errors.has('name')}" maxlength="30"
              v-validate="'required'"
              v-model="newAddress.name.given_name"
              autocomplete="name"
              @input="newAddress.name.given_name = formatName($event.target.value)">
        <span v-show="errors.has('name')" class="text-danger">{{ errors.first('name') }}</span>
      </div>

      <div class="col-sm-4 form-group">
        <label>Country*</label>
        <span class="styled-select">
          <select name="country"
                  id="countryCode"
                  type="text"
                  v-model="selectedCountry"
                  class="form-control"
                  :class="{'has-error' : errors.has('country')}"
                  v-validate="'required'"
                  autocomplete="country"
                  @change="updatedCountry">
            <option value="" disabled>Select country...</option>
            <option :value="{
                  code: 'USA',
                  long_name: 'United States',
                  region_name: 'State'
            }">United States</option>
            <option :value="{
                  code: 'CAN',
                  long_name: 'Canada',
                  region_name: 'Province'
            }">Canada</option>
            <option disabled>--------------</option>
            <option v-for="country in countries"
                    :value="country"
                    :key="country.code">
              {{ country.long_name }}
            </option>
          </select>
        </span>
        <span v-show="errors.has('country')" class="text-danger"
              style="float: left;">{{ errors.first('country') }}</span>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6 form-group" v-if="addressType === 'shipping'">
        <label>Country*</label>
        <span class="styled-select">
          <select name="country"
                  type="country"
                  id="countryCode"
                  v-model="selectedCountryChecked"
                  class="form-control"
                  :class="{'has-error' : errors.has('country')}"
                  v-validate="'required'"
                  autocomplete="country"
                  @change="updatedCountry">
            <option value="" disabled>Select country...</option>
            <option :value="{
                  code: 'USA',
                  long_name: 'United States',
                  region_name: 'State'
            }">United States</option>
            <option :value="{
                  code: 'CAN',
                  long_name: 'Canada',
                  region_name: 'Province'
            }">Canada</option>
            <option disabled>--------------</option>
            <option v-for="country in countries"
                    :value="country"
                    :key="country.code">
              {{ country.long_name }}
            </option>
          </select>
        </span>
        <span v-show="errors.has('country')" class="text-danger"
              style="float: left;">{{ errors.first('country') }}</span>
      </div>

      <div class="col-sm-6 form-group" v-if="addressType === 'shipping'">
        <label>Phone Number*</label>
        <input type="tel" name="tel" class="form-control" :class="{'has-error' : errors.has('phone_number')}"
              maxlength="30" v-model="newAddress.phone_number" v-validate="{ required: true }" autocomplete="tel"
              @input="newAddress.phone_number = formatPhone($event.target.value)">
        <span v-show="errors.has('phone_number')" class="text-danger" style="float: left;">
          {{ errors.first('phone_number') }}
        </span>
      </div>
    </div>

    <div class="row">
      <div class="col-sm-6 form-group">
        <label>Address*</label>
        <input type="text"
              name="addres-line1"
              class="form-control"
              :class="{'has-error' : errors.has('street_address')}"
              maxlength="30"
              v-validate="{ required: true }"
              v-model="newAddress.street_address"
              autocomplete="address-line1"
              @input="newAddress.street_address = formatAddress($event.target.value)">
        <span v-show="errors.has('street_address')" class="text-danger">{{ errors.first('street_address') }}</span>
      </div>

      <div class="col-sm-6 form-group">
        <label>Address Line 2</label>
        <input type="text"
              name="address-line2"
              class="form-control"
              maxlength="30"
              v-model="newAddress.street_address_2"
              autocomplete="address-line2"
              @input="newAddress.street_address_2 = formatAddress($event.target.value)">
      </div>
    </div>

    <div class="row">
      <div class="col-sm-4 form-group">
        <label>City*</label>
        <input type="text" name="city" class="form-control" :class="{'has-error' : errors.has('city')}" maxlength="20"
              v-validate="'required'"
              v-model="newAddress.city"
              @input="newAddress.city = formatAddress($event.target.value)">
        <span v-show="errors.has('city')" class="text-danger" style="float: left;">{{ errors.first('city') }}</span>
      </div>

      <div class="col-sm-4 form-group">
        <label>State{{ (regionCheck(newAddress.country_code)) ? '*' : '' }}</label>
        <span class="styled-select">
          <select name="address-level1"
                  type="text"
                  id="regionCode"
                  v-model="newAddress.region_code"
                  class="form-control"
                  :class="{'has-error' : errors.has('state')}"
                  v-validate="{'required' : regionCheck(newAddress.country_code)}"
                  autocomplete="address-level1"
                  :disabled="!isStateRequired">
            <option value="" disabled>
              {{ isStateRequired ? 'Select state...' : '' }}
            </option>
            <option v-for="region in regions" :value="region.code" :key="region.code">
              {{ region.name }}
            </option>
          </select>
        </span>
        <span v-show="errors.has('state')" class="text-danger" style="float: left;">{{ errors.first('state') }}</span>
      </div>

      <div class="col-sm-4 form-group">
        <label>Postal Code{{ (postalCheck(newAddress.country_code)) ? '*': '' }}</label>
        <input type="text" maxlength="10" name="postal-code" class="form-control" :class="{'has-error' : errors.has('postal_code')}" autocomplete="postal-code"
              @input="newAddress.postal_code = formatPostalCode($event.target.value)" v-validate="{'required' : postalCheck(newAddress.country_code)}"
              v-model="newAddress.postal_code">
        <span v-show="errors.has('postal_code')" class="text-danger" style="float: left;">{{ errors.first('postal_code') }}</span>
      </div>
    </div>

    <label class="checkbox-label" v-if="bools.newAddress && !isSageCard">
      <input type="checkbox" class="save-to-account" v-model="saveToAccount">Remember {{ addressType }} for next time
    </label>
    <br v-if="bools.newAddress && !isSageCard && addressType === 'shipping'">
    <button class="btn btn-primary" :disabled="bools.submitting" @click.prevent="saveAddress" v-if="!isSageCard && addressType === 'shipping'">
      {{ buttonLabel }}
      <i class="fa fa-spin fa-spinner" v-if="bools.submitting"></i>
    </button>
    <button class="btn" v-if="showCancelButton && !isSageCard && addressType === 'shipping'" @click.prevent="closeThisForm">
      Cancel
    </button>
  </div>
</template>

<script>
  import { mapGetters, mapMutations, mapActions } from 'vuex';

  import formatting from '../../mixins/formatting';

  export default {
    name: 'address-form',
    mixins: [formatting],
    props: {
      addressType:  String,
      numberOfAddresses: {
        type: Number,
        required: true
      },
      addressToValidate: Object,
      validateBillingAddress: Boolean,
      isSageCard: Boolean,
      countries: Array,
      postalCodesRequired: Array,
      regionCodesRequired: Array,
      editShippingAddress: Boolean,
    },
    data() {
      return {
        newAddress: {
          name: {
            given_name: ''
          },
          street_address: '',
          street_address_2: '',
          country_code: '',
          postal_code: '',
          phone_number: '',
          city: '',
          region_code: '',
          type: this.addressType
        },
        bools: {
          newAddress: true,
          submitting: false
        },
        selectedCountry: '',
        regions: [],
        editedAddress: {},
        formErrors: [],
        saveToAccount: true,
        isStateRequired: false,
      }
    },
    computed: {
      ...mapGetters([
        'selectedShippingAddress',
        'selectedBillingAddress',
        'billingSameAsShipping',
      ]),
      addressFormIsCompleted() {
        if (
          (this.newAddress.name.given_name !== '' &&
          this.newAddress.street_address !== '' &&
          this.newAddress.country_code!== '' &&
          this.newAddress.postal_code !== '' &&
          this.newAddress.city !== '') ? true : false
        ) {
          if(
            (this.isStateRequired && this.newAddress.region_code !== '') ||
            !this.isStateRequired
            ) {
            return true;
          }
        }

        return false;
      },
      addressNameRegex() {
        return new RegExp("[^A-Za-z0-9\\s:,#\\/\\-&\".+]", "g");
      },
      addressRegex() {
        return new RegExp("[^\\w'\\-\\s\\.,#\\/]", "gu");
      },
      postalRegex() {
        return new RegExp("[^\\w'\\-\\s\\-.\\/]", "gu");
      },
      nameRegex() {
        return /(^|[^\-a-z])n[\/\-\s\\]*a([^\-a-z]|$)/i;
      },
      poBoxRegex() {
        return /\b(?:p\.?\s*o\.?|post\s+office)(\s+)?(?:box|[0-9]*)?\b/i;
      },
      formattedAddress() {
        let formattedAddress = {
          city: this.newAddress.city,
          name: {
            given_name: this.newAddress.name.given_name
          },
          postal_code: this.newAddress.postal_code.replace(/_/g, ""),
          country_code: this.newAddress.country_code,
          region_code: this.newAddress.region_code,
          street_address: this.newAddress.street_address,
          street_address_2: this.newAddress.street_address_2,
          type: this.newAddress.type
        };

        if (this.addressType === 'shipping') {
          formattedAddress["phone_number"] = this.newAddress.phone_number;
        }

        return formattedAddress;
      },
      showCancelButton() {
        return !!(!this.formIsSelected && this.numberOfAddresses);
      },
      formIsSelected() {
        if (this.addressType === 'shipping') {
          return !!this.selectedShippingAddress && !this.selectedShippingAddress.id && !this.isSageCard;
        } else {
          return !!this.selectedBillingAddress && !this.selectedBillingAddress.id && !this.billingSameAsShipping && !this.isSageCard;
        }
      },
      buttonLabel() {
        if (this.formIsSelected || this.newAddress.id) {
          return 'Apply Changes';
        } else {
          return `Use this ${_.startCase(this.addressType)} Address`;
        }
      },
      selectedCountryChecked: {
        get() {
          if (this.selectedCountry === '') {
            return {};
          }

          return this.selectedCountry;
        },
        set(value) {
          this.selectedCountry = value;
        }
      }
    },
    mounted() {
      let vm = this;

      vm.$parent.$on('apply-billing-address', vm.applyBillingAddress);

      vm.$parent.$on('submitPaymentOption', async function() {
        try {
          await vm.saveAddress();
          vm.$emit('save-billing-to-payment', vm.formattedAddress);
        } catch(error) {
          console.error(error);
          vm.$emit('error-saving-billing-to-payment');
        }
      });

      if (this.editShippingAddress) {
        this.editAddress();
      }

      if (vm.validateBillingAddress) {
        vm.newAddress = {
          name: {
            given_name: vm.addressToValidate.name.given_name
          },
          street_address: vm.addressToValidate.street_address,
          street_address_2: vm.addressToValidate.street_address_2,
          country_code: vm.addressToValidate.country.code,
          postal_code: vm.addressToValidate.postal_code,
          phone_number: vm.addressToValidate.phone_number,
          city: vm.addressToValidate.city,
          region_code: vm.addressToValidate.region.code,
          type: vm.addressType
        };

        if (!vm.addressToValidate.postal_code) {
          vm.formErrors = ['Postal Code is missing, please add postal code and update address to card'];
        }

        let nameRegex = vm.nameRegex;
        if (nameRegex.test(vm.newAddress.name.given_name)) {
          if (vm.formErrors) {
            vm.formErrors = [vm.formErrors + '| Invalid Name. The name on the address cannot include N/A'];
          } else {
            vm.formErrors = ['Invalid Name. The name on the address cannot include N/A'];
          }
        }
      }
    },
    watch: {
      'selectedCountry.code'(code) {
        this.isStateRequired = ['CAN', 'USA', 'JAM', 'IND', 'AUS'].includes(code);

        if (this.isStateRequired) {
          this.getRegions();
        } else {
          // If user has switched from a country where state is
          // required to non required then remove the previously
          // filled value
          this.newAddress.region_code = '';
        }
      },
      addressToValidate: {
        deep:true,
        handler(address) {
          if (Object.keys(address).length > 0) {
            this.newAddress = {
              name: {
                given_name: address.name.given_name
              },
              street_address: address.street_address,
              street_address_2: address.street_address_2,
              country_code: address.country_code,
              postal_code: address.postal_code,
              phone_number: address.phone_number,
              city: address.city,
              region_code: address.region_code,
              type: this.addressType
            };
          }

          return;
        }
      },

      addressFormIsCompleted() {
        this.$emit('has-entered-payment-address', this.addressFormIsCompleted);
      }
    },
    methods: {
      ...mapMutations([
        'setShippingAddressError',
        'setBillingAddressError',
        'setAreShippingOptionsOutdated',
      ]),
      ...mapActions([
        'selectShippingAddressAction',
        'selectBillingAddressAction',
      ]),
      isValidNameChar() {
        this.newAddress.name.given_name = this.newAddress.name.given_name.replace(this.addressNameRegex, '');
      },
      isValidAddrCharacter(addressField) {
        switch (addressField) {
          case 1:
            this.newAddress.street_address = this.newAddress.street_address.replace(this.addressRegex, '');
            break;
          case 2:
            this.newAddress.street_address_2 = this.newAddress.street_address_2.replace(this.addressRegex, '');
            break;
        }
      },
      isValidPostalCharacter() {
        this.newAddress.postal_code = this.newAddress.postal_code.replace(this.postalRegex, '');
      },
      async saveAddress() {
        let editingAddress = !!this.newAddress.id;
        let success = await this.$validator.validateAll();

        if (!success) return Promise.reject('Invalid or missing form fields');

        this.bools.submitting = true;
        this.formErrors = null;
        this.verifyAddressLines();
        this.verifyName();

        if (this.formErrors) {
          this.bools.submitting = false;
          return Promise.reject('Invalid or missing form fields');
        }

        if (this.newAddress.type === 'shipping') {
          if (editingAddress) {
            this.selectShippingAddressAction(_.cloneDeep(this.newAddress));
            this.setAreShippingOptionsOutdated(false);
            this.sendAddressToApi('update');
          } else if (this.saveToAccount) {
            this.sendAddressToApi();
          } else {
            this.selectShippingAddressAction(_.cloneDeep(this.newAddress));
            this.bools.submitting = false;
            this.$emit('close-selector-and-form');
          }
        }

        return Promise.resolve();
      },
      applyBillingAddress() {
        if (this.saveToAccount) {
          this.sendAddressToApi();
        } else {
          this.selectBillingAddressAction(_.cloneDeep(this.newAddress));
        }
      },
      verifyAddressLines() {
        if (this.addressType === 'shipping') {
          let addressRegex = this.poBoxRegex;
          if (addressRegex.test(this.newAddress.street_address) || addressRegex.test(this.newAddress.street_address_2)) {
            this.formErrors = ['Invalid Address. The address cannot contain a PO Box.'];
          }
        }
      },
      verifyName() {
        let nameRegex = this.nameRegex;
        if (nameRegex.test(this.newAddress.name.given_name)) {
          if (this.formErrors) {
            this.formErrors = [this.formErrors + '| Invalid Name. The name on the address cannot include N/A'];
          } else {
            this.formErrors = ['Invalid Name. The name on the address cannot include N/A'];
          }
        }
      },
      postalCheck() {
        return _.includes(this.postalCodesRequired, this.newAddress.country_code);

      },
      regionCheck() {
        return _.includes(this.regionCodesRequired, this.newAddress.country_code);

      },
      async sendAddressToApi(action = 'new') {
        try {
          let response = '';
          let addressURL = '/api/proxify/me/addresses';

          if (action === 'update') {
            response = await axios.patch(`${addressURL}/${this.newAddress.id}`, this.formattedAddress);

            if (this.newAddress.type === 'shipping') {
              this.setShippingAddressError(false);
              this.$parent.$emit('editedShippingAddress');
              this.$root.$emit('editedShippingAddress', this.newAddress);
              this.$emit('close-selector-and-form');
            } else {
              this.setBillingAddressError(false);
            }
          } else {
            response = await axios.post(addressURL, this.newAddress);
          }

          const savedAddress = Object.assign({id: response.data.id}, this.newAddress);

          if (action === 'new') {
            this.$root.availableBillingAddresses.push(savedAddress);
          }

          this.reset();
          this.selectAddress(savedAddress);
        } catch (error) {
          console.error(error);
        } finally {
          this.bools.submitting = false;
        }

        this.$emit('close-selector-and-form');
      },

      selectAddress(address) {
        if (this.addressType === 'shipping') {
          this.selectShippingAddressAction(address);
        } else {
          this.selectBillingAddressAction(address);
        }
      },

      editAddress() {
        let vm = this;
        let address = vm.addressType === 'shipping' ? vm.selectedShippingAddress : vm.selectedBillingAddress;
        let countryCode = address.country ? address.country.code : address.country_code;
        let countries = vm.countries.filter(country => country.code === countryCode);

        vm.bools.newAddress = false;
        vm.newAddress = _.cloneDeep(address);
        vm.selectedCountry = countries.length ? countries[0] : '';
        vm.editedAddress = _.cloneDeep(address);

        vm.$validator.validateAll();
      },

      updatedCountry() {
          this.newAddress.country_code = this.selectedCountryChecked.code;
      },

      async getRegions() {
        const countryCode = ('country_code' in this.newAddress) ? this.newAddress.country_code : this.newAddress.country.code;
        const regionsUrl = '/api/proxify/domains/cerakote/shop/countries/' + countryCode + '/regions';

        try {
          let response = await axios.get(regionsUrl);
          this.regions = response.data;

          if (this.newAddress.id) {
            if (this.editedAddress.region) {
              this.newAddress.region_code = this.editedAddress.region.code;
            } else {
              this.newAddress.region_code = this.editedAddress.region_code;
            }
          }

          this.$validator.validateAll();
        } catch (e) {
          console.error(e);
        }
      },

      reset() {
        const addressType = this.newAddress.type; // cache the address type
        let vm = this;

        Object.assign(this.$data, this.$options.data(), {
          countries: this.countries,
          buttonLabel: this.buttonLabel
        });
        this.newAddress.type = addressType;

        setTimeout(function () {
          vm.errors.clear();
        }, 10);
      },

      closeThisForm() {
        this.reset();
        this.$emit('close-form');
        this.$parent.$emit('editedShippingAddress');
      }
    }
  }
</script>
